import React, { useCallback, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '../design-system';
import {
  ContentSafetyDialog,
  ContentSafetyTarget,
} from './ContentSafetyDialog';

interface ContentSafetyActionsProps {
  enabled: boolean;
  entityId?: string;
  entityType: ContentSafetyTarget['entityType'];
  hasEnabledMenuItems?: boolean;
  menuItems?: React.ReactNode;
  ownerUserId?: string | null;
}

const menuListProps = { dense: true };

export const ContentSafetyActions = React.memo(function ContentSafetyActions({
  enabled,
  entityId,
  entityType,
  hasEnabledMenuItems,
  menuItems,
  ownerUserId,
}: ContentSafetyActionsProps) {
  const intl = useIntl();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [target, setTarget] = useState<ContentSafetyTarget | null>(null);
  const canReport = enabled && Boolean(entityId && ownerUserId);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchor(null);
  };

  const handleDialogOpen = (report: boolean) => {
    if (!canReport || !entityId || !ownerUserId) {
      return;
    }

    setTarget({ entityId, entityType, ownerUserId, report });
  };

  const handleReportAndBlock = () => {
    handleDialogOpen(true);
  };

  const handleBlockUser = () => {
    handleDialogOpen(false);
  };

  const handleDialogClose = useCallback(() => {
    setTarget(null);
  }, []);

  return (
    <>
      <IconButton
        aria-expanded={Boolean(anchor)}
        aria-haspopup="menu"
        aria-label={intl.formatMessage({
          id: 'contentSafety.actions.more',
          defaultMessage: 'More actions',
        })}
        disabled={!canReport && !hasEnabledMenuItems}
        onClick={handleMenuOpen}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        MenuListProps={menuListProps}
        onClick={handleMenuClose}
        onClose={handleMenuClose}
        open={Boolean(anchor)}
      >
        {menuItems}
        {canReport && (
          <MenuItem disabled>
            <FormattedMessage
              id="contentSafety.action.report"
              defaultMessage="Report abuse"
            />
          </MenuItem>
        )}
        {canReport && (
          <MenuItem onClick={handleReportAndBlock}>
            <FormattedMessage
              id="contentSafety.action.reportAndBlock"
              defaultMessage="Report abuse and block user"
            />
          </MenuItem>
        )}
        {canReport && (
          <MenuItem onClick={handleBlockUser}>
            <FormattedMessage
              id="contentSafety.action.block"
              defaultMessage="Block user"
            />
          </MenuItem>
        )}
      </Menu>
      {target && (
        <ContentSafetyDialog onClose={handleDialogClose} target={target} />
      )}
    </>
  );
});
